import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { Space } from 'antd';
import Highlighter from 'react-highlight-words';
import { ColumnsType, SortOrder } from 'antd/es/table/interface';

import { ICategory } from '../../interfaces';
import CategoriesService from '../../services/http/services/CategoriesService';
import { EmitSuccessAlert } from '../../utils/EmitSuccessAlert';
import { EmitError } from '../../utils/EmitError';
import useAuth from '../../hooks/useAuth';
import { formatDateStringToDate, formatDateStringToDateAndTime } from '../../utils/Conversores';
import { removeAcento } from '../../utils/RemoveAcentos';
import { useGetColumnSearchPropsData } from '../../hooks/FilterTable/date';
import { ThemeContext } from '../../contexts/ThemeContext';
import TableActionButton from '../../components/TableActionButton';
import { IActiveFilterItem } from '../../components/ActiveFiltersBar';
import { findChunksText, HIGHLIGHT_STYLE } from '../../utils/Highlight';
import useTableColumnFilter from '../../hooks/useTableColumnFilter';

export interface ITableData extends ICategory {
    key: number;
}

export interface ICategoryFilters {
    name: string | null;
    description: string | null;
    created_at: string | null;
}

export type SortField = 'name' | 'description' | 'created_at' | null;

export default function useCategories() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const { activeCity } = useAuth();
    const navigate = useNavigate();
    const { getColumnSearchProps } = useTableColumnFilter();

    // Top-level custom search hooks
    const createdDateSearchProps = useGetColumnSearchPropsData({ dataIndex: 'created_at' });

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isLoadingDelete, setIsLoadingDelete] = useState<boolean>(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [categoryBeingDeleted, setCategoryBeingDeleted] = useState<ITableData | null>(null);
    const [rawData, setRawData] = useState<Array<ITableData>>([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 25 });

    const [filters, setFilters] = useState<ICategoryFilters>({
        name: null,
        description: null,
        created_at: null,
    });

    const [sorter, setSorter] = useState<{ field: SortField; order: 'asc' | 'desc' | null }>({
        field: 'name',
        order: 'asc',
    });

    const [searchName, setSearchName] = useState(filters.name || '');
    const [searchDescription, setSearchDescription] = useState(filters.description || '');

    useEffect(() => {
        setSearchName(filters.name || '');
    }, [filters.name]);

    useEffect(() => {
        setSearchDescription(filters.description || '');
    }, [filters.description]);

    const handleFilterChange = useCallback((key: keyof ICategoryFilters, value: any) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const handleClearAllFilters = useCallback(() => {
        setFilters({
            name: null,
            description: null,
            created_at: null,
        });
        setSearchName('');
        setSearchDescription('');
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const setTableDataFromRequest = useCallback((data: Array<ICategory>) => {
        if (!Array.isArray(data)) return;
        const newData: ITableData[] = data.map((category) => ({
            ...category,
            key: category.id || 0,
        }));
        setRawData(newData);
    }, []);

    useEffect(() => {
        if (!activeCity) return;

        const controller = new AbortController();

        async function loadCategories() {
            try {
                setIsLoading(true);
                const categories = await CategoriesService.listCategories(activeCity?.id!, controller.signal);
                setTableDataFromRequest(categories || []);
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }
                EmitError(err, true, 'categories_load_error');
            } finally {
                setIsLoading(false);
            }
        }

        loadCategories();

        return () => {
            controller.abort();
        };
    }, [activeCity, setTableDataFromRequest]);

    const filteredTableData = useMemo(() => {
        let result = rawData;

        if (filters.name) {
            const query = removeAcento(filters.name.trim().toLowerCase());
            result = result.filter((item) =>
                item.name ? removeAcento(item.name.toLowerCase()).includes(query) : false
            );
        }

        if (filters.description) {
            const query = removeAcento(filters.description.trim().toLowerCase());
            result = result.filter((item) =>
                item.description ? removeAcento(item.description.toLowerCase()).includes(query) : false
            );
        }

        if (filters.created_at) {
            const query = filters.created_at.trim();
            result = result.filter((item) => {
                if (!item.created_at) return false;
                const formatted = formatDateStringToDate(item.created_at);
                return formatted.includes(query);
            });
        }

        if (sorter.field && sorter.order) {
            result = [...result].sort((a, b) => {
                if (sorter.field === 'name') {
                    const comp = (a.name || '').localeCompare(b.name || '');
                    return sorter.order === 'asc' ? comp : -comp;
                }
                if (sorter.field === 'description') {
                    const comp = (a.description || '').localeCompare(b.description || '');
                    return sorter.order === 'asc' ? comp : -comp;
                }
                if (sorter.field === 'created_at') {
                    const valA = new Date(a.created_at || 0).getTime();
                    const valB = new Date(b.created_at || 0).getTime();
                    return sorter.order === 'asc' ? valA - valB : valB - valA;
                }
                return 0;
            });
        }

        return result;
    }, [rawData, filters, sorter]);

    const activeFilterItems: Array<IActiveFilterItem | null> = useMemo(() => [
        filters.name
            ? {
                key: 'name',
                label: 'Nome da Categoria',
                value: filters.name,
                onClear: () => {
                    handleFilterChange('name', null);
                    setSearchName('');
                },
            }
            : null,
        filters.description
            ? {
                key: 'description',
                label: 'Descrição',
                value: filters.description,
                onClear: () => {
                    handleFilterChange('description', null);
                    setSearchDescription('');
                },
            }
            : null,
        filters.created_at
            ? {
                key: 'created_at',
                label: 'Data de Cadastro',
                value: formatDateStringToDate(filters.created_at),
                onClear: () => handleFilterChange('created_at', null),
            }
            : null,
    ], [filters, handleFilterChange]);

    const getSortOrder = useCallback((field: SortField): SortOrder | undefined => {
        if (sorter.field === field) {
            return sorter.order === 'asc' ? 'ascend' : sorter.order === 'desc' ? 'descend' : undefined;
        }
        return undefined;
    }, [sorter]);

    const handleTableChange = (_: any, __: any, newSorter: any) => {
        if (newSorter && newSorter.field) {
            setSorter({
                field: newSorter.field as SortField,
                order: newSorter.order === 'ascend' ? 'asc' : newSorter.order === 'descend' ? 'desc' : null,
            });
        }
    };

    const handlePaginationChange = useCallback((page: number, pageSize: number) => {
        setPagination({ current: page, pageSize });
    }, []);

    const handleDeleteCategory = useCallback((record: ITableData) => {
        setCategoryBeingDeleted(record);
        setIsDeleteModalVisible(true);
    }, []);

    const handleConfirmDeleteCategory = useCallback(async () => {
        if (!categoryBeingDeleted?.id) return;
        try {
            setIsLoadingDelete(true);
            await CategoriesService.deleteCategory(categoryBeingDeleted.id);
            EmitSuccessAlert('Categoria excluída com sucesso!');
            setRawData((prev) => prev.filter((item) => item.id !== categoryBeingDeleted.id));
            setIsDeleteModalVisible(false);
            setCategoryBeingDeleted(null);
        } catch (err) {
            EmitError(err, true, 'categories_delete_error');
        } finally {
            setIsLoadingDelete(false);
        }
    }, [categoryBeingDeleted]);

    const handleCancelDeleteCategory = useCallback(() => {
        setIsDeleteModalVisible(false);
        setCategoryBeingDeleted(null);
    }, []);

    const handleClickCreateNewCategory = useCallback(() => {
        navigate('/categorias/criar');
    }, [navigate]);

    const columns: ColumnsType<ITableData> = useMemo(() => [
        {
            title: 'Nome da Categoria',
            dataIndex: 'name',
            key: 'name',
            sorter: true,
            sortOrder: getSortOrder('name'),
            showSorterTooltip: false,
            ...getColumnSearchProps({
                title: 'Buscar por Nome',
                placeholder: 'Nome da categoria...',
                value: searchName,
                onChange: setSearchName,
                onSearch: (val) => handleFilterChange('name', val),
                isActive: Boolean(filters.name),
            }),
            render: (name: string) => (
                <span
                    style={{
                        fontWeight: 600,
                        color: isDark ? '#f0f0f0' : '#1e293b',
                        fontSize: 13.5,
                    }}
                >
                    {filters.name ? (
                        <Highlighter
                            highlightStyle={HIGHLIGHT_STYLE}
                            searchWords={[filters.name]}
                            textToHighlight={name || '--'}
                            findChunks={findChunksText}
                        />
                    ) : (
                        name || '--'
                    )}
                </span>
            ),
        },
        {
            title: 'Descrição',
            dataIndex: 'description',
            key: 'description',
            sorter: true,
            sortOrder: getSortOrder('description'),
            showSorterTooltip: false,
            ...getColumnSearchProps({
                title: 'Buscar por Descrição',
                placeholder: 'Descrição da categoria...',
                value: searchDescription,
                onChange: setSearchDescription,
                onSearch: (val) => handleFilterChange('description', val),
                isActive: Boolean(filters.description),
            }),
            render: (description: string) => (
                <span
                    style={{
                        color: isDark ? '#cbd5e1' : '#475569',
                        fontSize: 13,
                    }}
                >
                    {filters.description ? (
                        <Highlighter
                            highlightStyle={HIGHLIGHT_STYLE}
                            searchWords={[filters.description]}
                            textToHighlight={description || '--'}
                            findChunks={findChunksText}
                        />
                    ) : (
                        description || '--'
                    )}
                </span>
            ),
        },
        {
            title: 'Data de Cadastro',
            dataIndex: 'created_at',
            key: 'created_at',
            sorter: true,
            sortOrder: getSortOrder('created_at'),
            showSorterTooltip: false,
            ...createdDateSearchProps,
            render: (date: any) => (
                <span style={{ fontSize: 12.5, whiteSpace: 'nowrap', color: isDark ? '#cbd5e1' : '#475569' }}>
                    {date ? formatDateStringToDateAndTime(date) : '--'}
                </span>
            ),
        },
        {
            title: 'Ações',
            key: 'actions',
            align: 'center' as const,
            width: 100,
            render: (_: any, record: ITableData) => (
                <Space size={4}>
                    <TableActionButton
                        action="edit"
                        tooltip="Editar categoria"
                        onClick={() => navigate(`/categorias/editar/${record.id}`)}
                    />
                    <TableActionButton
                        action="delete"
                        tooltip="Excluir categoria"
                        onClick={() => handleDeleteCategory(record)}
                    />
                </Space>
            ),
        },
    ], [
        isDark,
        filters,
        getSortOrder,
        searchName,
        searchDescription,
        getColumnSearchProps,
        createdDateSearchProps,
        handleFilterChange,
        handleDeleteCategory,
        navigate,
    ]);

    return {
        isLoading,
        isLoadingDelete,
        isDeleteModalVisible,
        categoryBeingDeleted,
        columns,
        tableData: filteredTableData,
        pagination,
        activeFilterItems,
        handleClickCreateNewCategory,
        handleConfirmDeleteCategory,
        handleCancelDeleteCategory,
        handleTableChange,
        handlePaginationChange,
        handleClearAllFilters,
        navigate,
    };
}
